import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { FieldValue, UseFormReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { ChangeEvent, MouseEvent } from 'react';
import { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, InputAdornment, Typography } from '@mui/material';
import { TextField } from 'components/common';
import { CopyAll } from '@mui/icons-material';
import { useInView } from 'react-intersection-observer';
import type { CampaignEntry } from '../api';
import { isUndefined } from 'lodash-es';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface FormData extends FieldValue<any> {
  selectedCampaigns: CampaignEntry[];
  selectedCampaignsBudgets: Record<string, string>;
}

interface CampaignBudgetFieldProps {
  cellParams: GridRenderCellParams<CampaignEntry>;
}

interface UseCampaignBudgetForm
  extends Pick<UseFormReturn<FormData>, 'setValue' | 'getValues'> {
  selectedCampaigns: CampaignEntry[];
  currentRow: CampaignEntry | undefined;
  isSelected: boolean;
  isCBO: boolean;
  fieldName: string;
}

const useCampaignBudgetForm = (currentId: string): UseCampaignBudgetForm => {
  const { watch, setValue, getValues } = useFormContext<FormData>();

  const selectedCampaigns = watch('selectedCampaigns');

  const currentRow = useMemo(
    () => selectedCampaigns?.find((r: CampaignEntry) => r.id === currentId),
    [currentId, selectedCampaigns],
  );

  const isSelected = !!currentRow;
  const isCBO = isSelected && currentRow.is_cbo;

  const fieldName = useMemo(
    () => `selectedCampaignsBudgets.${currentId}`,
    [currentId],
  );

  return {
    selectedCampaigns,
    currentRow,
    isSelected,
    isCBO,
    fieldName,
    setValue,
    getValues,
  };
};

const CampaignBudgetField: FC<CampaignBudgetFieldProps> = ({ cellParams }) => {
  const currentId: string = cellParams.row.id;

  const {
    selectedCampaigns,
    currentRow,
    isSelected,
    isCBO,
    fieldName,
    setValue,
    getValues,
  } = useCampaignBudgetForm(currentId);

  useEffect(() => {
    if (!isSelected || !isCBO) return;

    const currentValue: string = getValues(fieldName);
    const budgetValue = currentRow?.budget?.toString() ?? '';

    if (isUndefined(currentValue) && budgetValue) {
      setValue(fieldName, budgetValue, {
        shouldValidate: true,
      });
    }
  }, [getValues, setValue, isSelected, isCBO, fieldName, currentRow?.budget]);

  const handleApplyToAll = useCallback((): void => {
    const budgetToAll: string = getValues(fieldName) as string;
    const selectedCampaignsBudgetsValue: Record<string, string> = getValues(
      'selectedCampaignsBudgets',
    );

    if (!selectedCampaignsBudgetsValue || !budgetToAll) return;

    const values: Record<string, string> = Object.fromEntries(
      selectedCampaigns
        .filter((c) => c.is_cbo)
        .map(({ id }) => [id, budgetToAll]),
    );

    setValue('selectedCampaignsBudgets', values, { shouldValidate: true });
  }, [selectedCampaigns, setValue, getValues, fieldName]);

  const handleBoxClick = useCallback((e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
      const newBudgetValue = event.target.value;

      if (!newBudgetValue) return;

      const selectedCampaignsBudgetsValue: Record<string, string> = getValues(
        'selectedCampaignsBudgets',
      );

      setValue(
        'selectedCampaignsBudgets',
        {
          ...selectedCampaignsBudgetsValue,
          [currentId]: newBudgetValue,
        },
        { shouldValidate: true },
      );
    },
    [getValues, setValue, currentId],
  );

  const { ref, inView } = useInView({
    threshold: 0,
  });

  if (!isSelected) {
    return null;
  }

  if(!isCBO){
    return (
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={(t) => ({
            '&': { fontSize: t.typography.caption.fontSize },
          })}
        >
          Not applicable
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        height: '100%',
        gap: 1,
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'start',
        alignItems: 'center',
      }}
      onClick={handleBoxClick}
    >
      {inView && (
        <>
          <TextField
            errorAsTooltip
            handleChange={handleChange}
            name={fieldName}
            sx={{
              '& .MuiInputBase-root': {
                pr: 0.25,
                minWidth: 120,
              },
            }}
            InputProps={{
              type: 'number' as const,
              sx: {
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                  {
                    display: 'none',
                  },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    sx={(t) => ({
                      '&': { fontSize: t.typography.caption.fontSize },
                    })}
                  >
                    CBO $
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
          <Box className={'MuiInputAdornment-positionEnd'}>
            <Button
              data-testid={'applyToAllCampaignBudgetButton'}
              onClick={handleApplyToAll}
              size={'small'}
              sx={{ textTransform: 'initial', px: 1 }}
              startIcon={<CopyAll />}
              disabled={!isCBO}
            >
              Apply to all
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
export default CampaignBudgetField;

const arePropsEqual = (
  prevProps: CampaignBudgetFieldProps,
  nextProps: CampaignBudgetFieldProps,
): boolean => {
  const prevRow = prevProps.cellParams.row;
  const nextRow = nextProps.cellParams.row;

  return (
    prevRow.id === nextRow.id &&
    prevRow.budget === nextRow.budget &&
    prevRow.is_cbo === nextRow.is_cbo &&
    prevProps.cellParams.hasFocus === nextProps.cellParams.hasFocus
  );
};

export const MemoizedCampaignBudgetField = memo(
  CampaignBudgetField,
  (prevProps, nextProps) => arePropsEqual(prevProps, nextProps),
);
